import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "../../lib/api";
import { StatusBadge } from "../../components/driver/StatusBadge";

export default function AdminDispatch() {
  const [jobs, setJobs] = useState([]);
  const [drivers, setDrivers] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [drafts, setDrafts] = useState({});

  const load = async () => {
    try {
      const [jobRes, driverRes, vehicleRes] = await Promise.all([
        api.get("/admin/driver-jobs"),
        api.get("/admin/drivers"),
        api.get("/admin/vehicles"),
      ]);
      setJobs(jobRes.data.jobs || []);
      setDrivers(driverRes.data || []);
      setVehicles(vehicleRes.data || []);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  useEffect(() => { load(); }, []);

  const updateDraft = (jobId, key, value) => setDrafts((prev) => ({ ...prev, [jobId]: { ...prev[jobId], [key]: value } }));

  const applyChanges = async (job) => {
    const draft = drafts[job.id] || {};
    const payload = {
      assigned_driver_id: draft.assigned_driver_id || job.assigned_driver_id || null,
      assigned_vehicle_id: draft.assigned_vehicle_id || job.assigned_vehicle_id || null,
      planned_start_time: draft.planned_start_time || job.planned_start_time,
      planned_end_time: draft.planned_end_time || job.planned_end_time,
      override_active_job: false,
    };
    const isActive = ["en_route", "arrived", "in_progress", "issue_reported"].includes(job.status);
    if (isActive) {
      const confirmed = window.confirm("This job is active. Reassign/reschedule anyway?");
      if (!confirmed) return;
      payload.override_active_job = true;
      payload.note = "Admin override confirmed from dispatch board";
    }
    try {
      await api.patch(`/admin/driver-jobs/${job.id}`, payload);
      toast.success("Dispatch updated");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  return (
    <div className="space-y-6" data-testid="admin-dispatch-page">
      <div>
        <h1 className="font-heading text-2xl font-semibold tracking-tight">Dispatch</h1>
        <p className="text-sm text-slate-500">Today’s driver jobs, actual timing, and live execution status.</p>
      </div>
      <div className="grid gap-4 xl:grid-cols-2">
        {jobs.map((job) => (
          <div key={job.id} className="rounded-xl border border-slate-200 bg-white p-5">
            <div className="flex items-start justify-between gap-3">
              <div>
                <p className="text-xs font-bold uppercase tracking-[0.18em] text-sky-700">{job.booking_reference_snapshot || job.id}</p>
                <h2 className="mt-2 font-heading text-xl font-semibold tracking-tight">{job.customer_name_snapshot || "Driver job"}</h2>
                <p className="mt-1 text-sm text-slate-500">{new Date(job.planned_start_time).toLocaleString()}</p>
              </div>
              <StatusBadge status={job.status} testId={`admin-dispatch-status-${job.id}`} />
            </div>
            <div className="mt-4 grid gap-2 text-sm text-slate-600 sm:grid-cols-2">
              <p><span className="font-semibold text-slate-900">Driver:</span> {job.assigned_driver_name || job.assigned_driver_id || "Unassigned"}</p>
              <p><span className="font-semibold text-slate-900">Vehicle:</span> {job.assigned_vehicle_name || job.assigned_vehicle_id || "Unassigned"}</p>
              <p><span className="font-semibold text-slate-900">Planned:</span> {new Date(job.planned_start_time).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })} → {new Date(job.planned_end_time).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}</p>
              <p><span className="font-semibold text-slate-900">Actual:</span> {job.actual_start_time ? new Date(job.actual_start_time).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }) : "—"} → {job.actual_end_time ? new Date(job.actual_end_time).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }) : "—"}</p>
            </div>
            <div className="mt-4 grid gap-3 sm:grid-cols-2">
              <select value={drafts[job.id]?.assigned_driver_id || job.assigned_driver_id || ""} onChange={(e) => updateDraft(job.id, "assigned_driver_id", e.target.value)} data-testid={`admin-dispatch-driver-${job.id}`} className="h-10 rounded-md border border-slate-300 px-3 text-sm outline-none focus:border-slate-900">
                <option value="">Unassigned driver</option>
                {drivers.map((driver) => <option key={driver.id} value={driver.id}>{driver.name}</option>)}
              </select>
              <select value={drafts[job.id]?.assigned_vehicle_id || job.assigned_vehicle_id || ""} onChange={(e) => updateDraft(job.id, "assigned_vehicle_id", e.target.value)} data-testid={`admin-dispatch-vehicle-${job.id}`} className="h-10 rounded-md border border-slate-300 px-3 text-sm outline-none focus:border-slate-900">
                <option value="">Unassigned vehicle</option>
                {vehicles.map((vehicle) => <option key={vehicle.id} value={vehicle.id}>{vehicle.name}</option>)}
              </select>
              <input type="datetime-local" value={(drafts[job.id]?.planned_start_time || job.planned_start_time || "").slice(0,16)} onChange={(e) => updateDraft(job.id, "planned_start_time", new Date(e.target.value).toISOString())} data-testid={`admin-dispatch-start-${job.id}`} className="h-10 rounded-md border border-slate-300 px-3 text-sm outline-none focus:border-slate-900" />
              <input type="datetime-local" value={(drafts[job.id]?.planned_end_time || job.planned_end_time || "").slice(0,16)} onChange={(e) => updateDraft(job.id, "planned_end_time", new Date(e.target.value).toISOString())} data-testid={`admin-dispatch-end-${job.id}`} className="h-10 rounded-md border border-slate-300 px-3 text-sm outline-none focus:border-slate-900" />
            </div>
            <button onClick={() => applyChanges(job)} data-testid={`admin-dispatch-apply-${job.id}`} className="mt-4 w-full rounded-md bg-slate-900 px-4 py-3 text-sm font-semibold text-white hover:bg-slate-800">
              Apply dispatch changes
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}